import type { Ticker } from '@cutr/constants/cutlist-theme.ts';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthStore } from '@/api/login';
import { useCutlistState } from '@/api/store';
import { hydrateStoreFn, resetStoreFn } from '@/hooks';
import { Button } from '@/primitives/Button';
import {
  ArrowLeft,
  Checkmark,
  Cogwheel,
  DoubleCheckmark,
  Icon,
  Logout,
  Plus,
  Trash,
} from '@/primitives/Icons';
import {
  useAgentCutlistPatch,
  useCancelCutlist,
  useFinaliseOrder,
  useUpdateCutlistAgent,
} from '@/queries/agent';
import { useCreateEmptyCutlistByAgent } from '@/queries/crud';
import { getSourceConfig, useCurrentFeatures, useTicker } from '@/theme';

import { api } from '../api/backend';
import { ConfirmationDialog } from './ConfirmationDialog';
import { CutlistOwnerButton } from './CutlistOwnerButton';
import { FinaliseOrderDialog } from './FinaliseOrderDialog';

export const CutlistAgentHeader = () => {
  const { t } = useTranslation();
  const config = getSourceConfig();
  const Logo = config.logo;
  const location = useLocation();
  const [isPending, setIsPending] = React.useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const resetStore = resetStoreFn();
  const { mutateAsync: updateCutlist } = useUpdateCutlistAgent();
  const { mutateAsync: patchCutlist } = useAgentCutlistPatch(id!);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
  const [isConfirmFinaliseDialogOpen, setIsConfirmFinaliseDialogOpen] =
    React.useState(false);
  const { mutateAsync: cancelCutlist } = useCancelCutlist();
  const { mutateAsync: finaliseOrder } = useFinaliseOrder();
  const { agentQuoteFlow, agentMaterialUpload } = useCurrentFeatures();
  const { status, thirdPartyId } = useCutlistState();
  const source = useTicker();

  const isEditing =
    location.pathname.includes('edit') || location.pathname.includes('quote');
  const isOrdersPage = location.pathname.includes('orders') && !id;
  const isSettingsPage = location.pathname.includes('settings');
  const { mutateAsync } = useCreateEmptyCutlistByAgent();
  const hydrateStore = hydrateStoreFn();

  const canSaveProgress = !agentQuoteFlow; // this will be removed in the future but, right now, we only allow saving progress while moving through the quote flow

  const handleSave = async () => {
    setIsPending(true);
    if (!id) return;
    updateCutlist({ id })
      .then(() => {
        toast.success(t('common.cta.orderSaved'));
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  const handleDiscard = () => {
    resetStore();
    navigate(`/admin/cutlists/orders`);
  };

  const handleCreateNewCutlist = async () => {
    const cutlist = await mutateAsync();
    hydrateStore(cutlist);
    navigate(
      agentQuoteFlow
        ? `/admin/cutlists/orders/${cutlist.id}/quote/parts`
        : `/admin/cutlists/orders/${cutlist.id}/edit`,
      {
        state: { newOrder: true },
      }
    );
  };

  const confirmCancelOrder = () => {
    if (!id) {
      return;
    }

    cancelCutlist({ id })
      .then(() => {
        setIsConfirmDialogOpen(false);
        navigate('/admin/cutlists/orders');
      })
      .catch((e) => {
        if (e.response.status === 409) {
          toast.error(t('agent.deletionDialog.error'));
        }
      });
  };

  const confirmFinaliseOrder = async (dialogThirdPartyId?: string) => {
    if (!id) {
      return;
    }

    try {
      if (dialogThirdPartyId !== undefined) {
        await patchCutlist({
          id,
          fields: {
            thirdPartyId: dialogThirdPartyId,
          },
        });
      }

      await finaliseOrder({ id });
      navigate('/admin/cutlists/orders');
    } catch (error) {
      toast.error(t('agent.finaliseOrderDialog.toasts.error'));
    } finally {
      setIsConfirmFinaliseDialogOpen(false);
    }
  };

  return (
    <>
      <header>
        <a href={'/admin/cutlists/orders'} className="logo">
          <Logo />
        </a>
        <div
          className="flexAlign"
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginLeft: '3rem',
          }}
        >
          <div>
            {id && isEditing && !agentQuoteFlow && (
              <CutlistOwnerButton readOnly={status !== 'draft'} />
            )}
          </div>
          <div style={{ marginLeft: 'auto' }} className="flexAlign">
            {isOrdersPage && (
              <>
                <Button
                  icon={<Icon icon={<Plus />} />}
                  className="smaller header"
                  style={{
                    background: 'var(--header-cta-background)',
                    color: 'var(--header-cta-color)',
                  }}
                  onClick={() => handleCreateNewCutlist()}
                >
                  {t('common.newOrder')}
                </Button>
                {agentMaterialUpload && (
                  <Button
                    variant="header"
                    icon={<Icon icon={<Cogwheel />} />}
                    onClick={() => navigate('/admin/cutlists/settings')}
                  >
                    {t('agent.settings.cta')}
                  </Button>
                )}
              </>
            )}
            {isSettingsPage && (
              <Button
                variant="header"
                icon={<Icon icon={<ArrowLeft />} />}
                onClick={() => navigate('/admin/cutlists/orders')}
              >
                {t('common.cta.backTo', { name: t('common.cta.orders') })}
              </Button>
            )}
            {isEditing && (
              <>
                {source === 'SWISSKRONO' && status === 'quote_ready' && (
                  <>
                    <FinaliseOrderDialog
                      key={thirdPartyId}
                      isOpen={isConfirmFinaliseDialogOpen}
                      onClose={() => setIsConfirmFinaliseDialogOpen(false)}
                      onConfirm={confirmFinaliseOrder}
                      thirdPartyId={thirdPartyId}
                    />
                    <Button
                      variant="header"
                      style={{
                        color: 'var(--color-text)',
                      }}
                      icon={
                        <Icon
                          style={{ color: 'var(--green-5)' }}
                          icon={<DoubleCheckmark />}
                        />
                      }
                      onClick={() => setIsConfirmFinaliseDialogOpen(true)}
                      disabled={isPending}
                    >
                      {t('agent.finaliseOrderDialog.cta')}
                    </Button>
                  </>
                )}
                <ConfirmationDialog
                  isOpen={isConfirmDialogOpen}
                  onClose={() => setIsConfirmDialogOpen(false)}
                  onConfirm={confirmCancelOrder}
                  title={t('agent.deletionDialog.title')}
                  variant="alert"
                >
                  {t('agent.deletionDialog.content')}
                </ConfirmationDialog>
                <Button
                  variant="header"
                  style={{
                    border: '1px solid var(--red-5)',
                    color: 'var(--red-5)',
                  }}
                  icon={<Icon icon={<Trash />} />}
                  onClick={() => setIsConfirmDialogOpen(true)}
                  disabled={isPending}
                >
                  {t('agent.deletionDialog.cta')}
                </Button>
                {canSaveProgress && (
                  <Button
                    variant="header"
                    icon={<Icon icon={<Checkmark />} />}
                    onClick={handleSave}
                    disabled={isPending}
                  >
                    {'Save'}
                  </Button>
                )}
                <Button
                  variant="header"
                  icon={<Icon icon={<ArrowLeft />} />}
                  onClick={handleDiscard}
                  disabled={isPending}
                >
                  {t('common.cta.backTo', { name: t('common.cta.orders') })}
                </Button>
              </>
            )}
            <LogoutBtn />
            <Suspense fallback={'...'}>
              <LanguageSelector />
            </Suspense>
          </div>
        </div>
      </header>
    </>
  );
};

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const theme = useTicker();
  const currentLng = i18n.resolvedLanguage;

  const languagesForTicker = (ticker: Ticker) => {
    if (ticker === 'SWISSKRONO') return ['en', 'de'];
    if (ticker === 'CUTR') return ['en', 'de', 'nl'];

    return ['en', 'nl'];
  };

  const changeLanguage = async (lng: string) => {
    i18n.changeLanguage(lng);
    await api.updateUserLeadLocale();
  };

  return (
    <div className="flexAlign gap-xxs">
      {languagesForTicker(theme).map((lng) => {
        const style =
          lng === currentLng
            ? {
                fontWeight: 'bold',
                textDecoration: 'underline',
              }
            : undefined;
        return (
          <Button
            key={lng}
            variant="iconHeader"
            onClick={async () => await changeLanguage(lng)}
            style={style}
          >
            {t(`common.lng.${lng}`)}
          </Button>
        );
      })}
    </div>
  );
};

const LogoutBtn = () => {
  const { logout } = useAuthStore();
  const { t } = useTranslation();
  const { agent } = useAuthStore();

  const onLogout = () => {
    logout();
  };

  return (
    <Button
      variant="header"
      icon={<Icon icon={<Logout />} />}
      onClick={onLogout}
      className="tooltipBottom"
      tooltip={agent?.email}
    >
      {t('common.logout')}
    </Button>
  );
};
